import { useCallback, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import ArrowIOSRight from '@crehana/icons/outline/ArrowIosRight';
import ArrowRight from '@crehana/icons/outline/ArrowRight';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Col, Container, type CuiTheme, InverseButton, Row } from '@crehana/ui';
import { notEmpty } from '@crehana/utils';
import { useInterests } from '@crehana/web/components/InterestsPicker/hooks';

import type { CampaignProps } from '@/views/Campaign/types';

// We should change this implementation to something designed for this page.
import useCoursesQuery from './hooks/useCoursesQuery';
import Loader from './Loader';
import {
  CategoriesIconContainer,
  CustomChip,
  IconWrapper,
  RightContent,
  Wrapper,
} from './Packs.styles';
import PacksCarousel from './PacksCarousel';

const MyArrowIos = () => (
  <ArrowIOSRight
    className="my-8 sm:my-0 sm:mx-8"
    css={`
      ${({ theme }: { theme: CuiTheme }) => theme.breakpoints.down('sm')} {
        transform: rotate(90deg);
      }
    `}
    size={24}
    color="white"
  />
);

const Packs: React.FC<React.PropsWithChildren<CampaignProps['packs']>> = ({
  backgroundDesktop,
  backgroundMobile,
  callToAction,
  callToActionUrl,
  description,
  title,
  steps,
}) => {
  const { logEvent } = useAmplitude();
  const { categories, loading } = useInterests();
  const { data, getCourses } = useCoursesQuery({ first: 5 });
  const courses = data?.courses?.edges.map(e => e?.node).filter(notEmpty) || [];

  const [selectedCategory, setSelectedCategory] = useState<null | string>(null);
  const handleCtaClick = () => {
    logEvent({
      event: 'Click Packs CTA',
      data: {
        ...getSourceEventParams(),
        'CTA Url': callToActionUrl,
        'CTA Label': callToAction,
      },
    });
    return true;
  };

  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  const handleCategoryPick = useCallback((slug: string) => {
    setSelectedCategory(slug);
    getCourses(slug, 5);
  }, []);

  if (loading || !categories.length) {
    return <Loader />;
  }

  const CategoriesIcons = (
    <CategoriesIconContainer className="flex justify-start w-full overflow-x-scroll ml-16 sm:ml-0 pb-16 mt-48">
      {categories.map(({ icon, slug, originalId }) => {
        const isSelected = selectedCategory === slug;

        return (
          <div
            key={`packs-${originalId}`}
            className={`flex items-center justify-center flex-shrink-0 rounded-10 w-40 h-40 mr-16 ${
              isSelected ? 'bg-white' : 'bg-base-light-dark-mode'
            }`}
          >
            {icon && (
              <button
                type="button"
                onClick={() => handleCategoryPick(slug)}
                disabled={isSelected}
              >
                <IconWrapper
                  isSelected={isSelected}
                  dangerouslySetInnerHTML={{
                    __html: icon,
                  }}
                />
              </button>
            )}
          </div>
        );
      })}
    </CategoriesIconContainer>
  );

  return (
    <Wrapper
      id="packs-section"
      className="lg:flex items-center pb-64 lg:py-0 relative"
      bgDesktop={backgroundDesktop}
      bgMobile={backgroundMobile}
    >
      <Container>
        <Row>
          <Col lg={5} className="hidden lg:block">
            {CategoriesIcons}
            {courses.length && <PacksCarousel courses={courses} />}
          </Col>
          <Col lg={7} className="flex">
            <RightContent className="text-center m-auto lg:text-left lg:ml-auto lg:mr-0">
              <h2 className="font-h1 mt-32 lg:mt-0 text-white">
                {title || t('PACKS_TITLE')}
              </h2>
              <p className="font-body2 my-32 text-white">
                {description || t('PACKS_SUBTITLE')}
              </p>
              <div className="flex items-center flex-col sm:flex-row mb-48">
                <CustomChip text={steps ? steps[0] : t('PACKS_STEP1')} />
                <MyArrowIos />
                <CustomChip text={steps ? steps[1] : t('PACKS_STEP2')} />
                <MyArrowIos />
                <CustomChip text={steps ? steps[2] : t('PACKS_STEP3')} />
              </div>
              <InverseButton
                leftIcon={{ component: ArrowRight, size: 20 }}
                isWhite
                as="a"
                size="l"
                href={callToActionUrl || '/arma-tu-pack/'}
                label={callToAction}
                onClick={handleCtaClick}
              />
            </RightContent>
          </Col>
        </Row>
      </Container>
      <div
        className="lg:hidden m-auto"
        css={`
          max-width: 720px;
        `}
      >
        {CategoriesIcons}
      </div>
      <div className="lg:hidden">
        {courses.length && <PacksCarousel courses={courses} />}
      </div>
    </Wrapper>
  );
};

export default Packs;
